import React, { useEffect, useState } from "react";
import { X, XCircle, Calendar as CalendarIcon, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { getUnavailabilityDetails, GetUnavailabilityDetailsResponse } from "../../../../redux/features/unavailability/api";
import { ApprovalHistory } from "../../../../components/notificationv2";
import { DateTimeUtil } from "../../../../utils";
import { SchedulingToolUtils } from "../../../../utils/schedulingToolConfig";

/** Minimal data needed to open the modal and call the details API */
export interface UnavailabilityModalData {
    unavail: Record<string, unknown> & {
        unavailability_id?: string;
        start_date?: string;
    };
}

export interface UnavailabilityDetailModalProps {
    open: boolean;
    onClose: () => void;
    data: UnavailabilityModalData | null;
    formatDateString: (dateStr: string) => string;
    /** Called when user clicks Withdraw (shown when the date is pending/approved and has not already been withdrawn). Notes are optional. */
    onWithdraw?: (params: { unavailabilitySlotId: string; date: string; notes?: string }) => void;
    /** True while withdraw API is in progress; show loader and disable actions */
    isWithdrawing?: boolean;
    /** Current user (for approval actor + superadmin check). If not passed, Withdraw is hidden. */
    user?: any;
}

const REPEAT_DAY_MAP: Record<string, string> = {
    "0": "Sunday",
    "1": "Monday",
    "2": "Tuesday",
    "3": "Wednesday",
    "4": "Thursday",
    "5": "Friday",
    "6": "Saturday",
    sun: "Sunday",
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
};

function formatTimeRangeFromApi(
    allDay: string | undefined,
    startDate: string | undefined,
    endDate: string | undefined,
    fromTime: string | null | undefined,
    toTime: string | null | undefined
): { isFullDay: boolean; timeOrDurationText: string } {
    const isFullDay = allDay === "1" || allDay === "true" || !fromTime || !toTime;
    if (isFullDay) {
        return { isFullDay: true, timeOrDurationText: "Full Day" };
    }
    const baseStartDate = startDate || "2000-01-01";
    const baseEndDate = endDate || startDate || "2000-01-01";
    const startDateTime = `${baseStartDate} ${fromTime}`;
    const endDateTime = `${baseEndDate} ${toTime}`;
    const formatted = DateTimeUtil.formatElegantTimeRange(startDateTime, endDateTime);
    if (formatted && formatted !== "N/A") {
        return { isFullDay: false, timeOrDurationText: formatted };
    }
    const startHHmm = fromTime.length >= 5 ? fromTime.slice(0, 5) : fromTime;
    const endHHmm = toTime.length >= 5 ? toTime.slice(0, 5) : toTime;
    const fromFormatted = DateTimeUtil.getFormattedTime(`2000-01-01 ${startHHmm}:00`, "N/A");
    const toFormatted = DateTimeUtil.getFormattedTime(`2000-01-01 ${endHHmm}:00`, "N/A");
    return {
        isFullDay: false,
        timeOrDurationText: fromFormatted !== "N/A" && toFormatted !== "N/A"
            ? `${fromFormatted} – ${toFormatted}`
            : `${startHHmm} – ${endHHmm}`,
    };
}

function parseRepeatDaysDisplay(repeatDays: string | undefined): string {
    if (!repeatDays) return "Not specified";
    let days: string[] = [];
    try {
        if (repeatDays.includes(",")) {
            days = repeatDays.split(",").map((d) => d.trim());
        } else if (repeatDays.includes("[")) {
            days = JSON.parse(repeatDays);
        } else {
            days = [repeatDays];
        }
    } catch {
        days = [repeatDays];
    }
    const dayNames = days.map((d) => REPEAT_DAY_MAP[d.toLowerCase()] || d).join(", ");
    return dayNames || repeatDays;
}

/** "Repeats every Wednesday" or "Repeats every Wednesday, Friday" */
function getRepeatEveryText(repeatDays: string | undefined): string {
    const days = parseRepeatDaysDisplay(repeatDays);
    if (!days || days === "Not specified") return "";
    return `Repeats every ${days}`;
}

/** Format date range: "Feb 25 – Feb 28, 2026" */
function formatDateRangeWithYear(start: string, end: string): string {
    if (!start || !end) return "—";
    if (start === end) {
        return DateTimeUtil.formatWithDayjs(start, "MMMM D, YYYY");
    }
    const startStr = DateTimeUtil.formatWithDayjs(start, "MMM D");
    const endStr = DateTimeUtil.formatWithDayjs(end, "MMM D");
    const year = DateTimeUtil.formatWithDayjs(start, "YYYY");
    return `${startStr} – ${endStr}, ${year}`;
}

/** True if current user is one of the approval actors in workflow_details (actor_json). */
function isApprovalActorInWorkflow(user: any, workflowDetails: any[] | undefined): boolean {
    if (!user || !workflowDetails?.length) return false;
    const userIds = [
        user.id,
        user.userAccountId,
        user.os_emp_account_id,
        user.employee_id,
    ]
        .filter(Boolean)
        .map(String);
    if (userIds.length === 0) return false;
    return workflowDetails.some((step) => {
        const actors = Object.values(step.actor_json || {});
        return actors.some((actor: any) => {
            const actorId =
                actor?.employee_id ?? actor?.id ?? actor?.emp_account_id;
            return actorId != null && userIds.includes(String(actorId));
        });
    });
}

/** True if current user is the employee who owns this unavailability (details.employee). */
function isUnavailabilityEmployee(
    user: any,
    details: GetUnavailabilityDetailsResponse["data"] | null
): boolean {
    if (!user || !details?.employee) return false;
    const employeeId =
        (details.employee as any)?.employee_id ??
        (details.employee as any)?.id ??
        (details.employee as any)?.os_emp_account_id;
    if (employeeId == null || String(employeeId).trim() === "") return false;
    const userIds = [
        user.id,
        user.userAccountId,
        user.os_emp_account_id,
        user.employee_id,
    ]
        .filter(Boolean)
        .map(String);
    return userIds.includes(String(employeeId));
}

/** Get status/note from root-level date_status and date_note. Status: 1 = Approved, -1 = Rejected, 0 or other = Pending. */
function getDateStatusInfo(
    details: GetUnavailabilityDetailsResponse["data"] | null,
    _dateStr: string | undefined
): { dateStatus: number | undefined; dateNote: string | null | undefined; statusLabel: string; canWithdraw: boolean } {
    const dateStatus = details?.date_status;
    const dateNote = details?.date_note ?? null;
    const statusLabel =
        dateStatus === 1 ? "Approved" : dateStatus === -1 ? "Rejected" : "Pending";
    const canWithdraw = (dateStatus === 0 || dateStatus === 1) && (dateNote == null || dateNote === "");
    return { dateStatus, dateNote, statusLabel, canWithdraw };
}

const UnavailabilityDetailModal: React.FC<UnavailabilityDetailModalProps> = ({
    open,
    onClose,
    data,
    formatDateString,
    onWithdraw,
    isWithdrawing = false,
    user,
}) => {
    const [details, setDetails] = useState<GetUnavailabilityDetailsResponse["data"] | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    const [showWithdrawNotes, setShowWithdrawNotes] = useState(false);
    const [withdrawNotes, setWithdrawNotes] = useState("");

    useEffect(() => {
        if (!open || !data?.unavail?.unavailability_id || !data.unavail.start_date) {
        setDetails(null);
        setDetailsError(null);
        setShowWithdrawNotes(false);
        setWithdrawNotes("");
        return;
    }
    const slotId = Number(data.unavail.unavailability_id);
        if (!Number.isInteger(slotId)) return;

        setDetails(null);
        setDetailsLoading(true);
        setDetailsError(null);
        getUnavailabilityDetails({
            unavailability_slot_id: slotId,
            date: data.unavail.start_date,
        })
            .then((res: GetUnavailabilityDetailsResponse) => {
                if (res.status && res.data) {
                    setDetails(res.data);
                } else {
                    setDetails(null);
                    setDetailsError(res.message || "Failed to load details");
                }
            })
            .catch(() => {
                setDetails(null);
                setDetailsError("Failed to load details");
            })
            .finally(() => setDetailsLoading(false));
    }, [open, data?.unavail?.unavailability_id, data?.unavail?.start_date]);

    if (!open || !data) return null;

    const isLoading = detailsLoading && !details;
    const hasError = detailsError && !details;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1500]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] m-4 flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <XCircle className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                Unavailability
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                                <p className="text-sm text-gray-500">
                                    {details?.employee
                                        ? [details.employee.first_name, details.employee.last_name].filter(Boolean).join(" ") || "—"
                                        : "—"}
                                </p>
                                {details && (() => {
                                    const step = details.workflow_current_step_details;
                                    const dateStatusInfo = getDateStatusInfo(details, data?.unavail?.start_date);
                                    const currentStatus =
                                        step?.current_status != null ? Number(step.current_status) : dateStatusInfo.dateStatus;
                                    const statusLabel =
                                        step?.status_text != null && String(step.status_text).trim() !== ""
                                            ? String(step.status_text).trim()
                                            : dateStatusInfo.statusLabel;
                                    const dotClass =
                                        currentStatus === 1 ? "bg-green-500" : currentStatus === -1 ? "bg-red-500" : "bg-amber-500";
                                    return (
                                        <span className={`inline-flex items-center gap-1.5`}>
                                            <span className={`h-2 w-2 rounded-full ${dotClass}`} aria-hidden />
                                            <span className="text-xs font-medium text-gray-600">{statusLabel}</span>
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Modal Content - all from API */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {isLoading && (
                        <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
                            <Loader2 className="w-6 h-6 animate-spin flex-shrink-0" />
                            <span>Loading details...</span>
                        </div>
                    )}

                    {hasError && (
                        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                            <p className="text-sm text-amber-800">{detailsError}</p>
                        </div>
                    )}

                    {details && !isLoading && (() => {
                        const currentDate = data.unavail.start_date;
                        const dateStatusInfo = getDateStatusInfo(details, currentDate);
                        return (
                        <>
                            {/* Unavailability Type & Time */}
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <XCircle className="w-5 h-5 text-gray-600" />
                                    <h4 className="font-semibold text-gray-900">
                                        Unavailable
                                    </h4>
                                </div>
                                <div className="text-sm text-gray-700">
                                    {(() => {
                                        const { isFullDay, timeOrDurationText } = formatTimeRangeFromApi(
                                            details.all_day,
                                            details.start_date,
                                            details.end_date,
                                            details.from_time,
                                            details.to_time
                                        );
                                        if (isFullDay && details.start_date) {
                                            const dateStr = DateTimeUtil.formatWithDayjs(details.start_date, "MMM D, YYYY");
                                            return (
                                                <span>
                                                    {timeOrDurationText} • {dateStr}
                                                </span>
                                            );
                                        }
                                        return <span>{timeOrDurationText}</span>;
                                    })()}
                                </div>
                            </div>

                            {/* Date / Date Range */}
                            <div className="pt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {details.start_date && details.end_date && details.start_date !== details.end_date
                                        ? "Date Range"
                                        : "Date"}
                                </label>
                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-sm text-gray-700">
                                    {details.start_date && (
                                        <div className="flex items-start gap-2">
                                            <CalendarIcon size={14} className="flex-shrink-0 mt-0.5" />
                                            <span>
                                                {formatDateRangeWithYear(
                                                    details.start_date,
                                                    details.end_date || details.start_date
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Repeating */}
                            {(details.repeating === "1" || String(details.repeating) === "1") && details.repeat_days && (
                                <div className="pt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Repeating
                                    </label>
                                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                                        <div className="flex items-start gap-2 text-sm text-purple-900">
                                            <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
                                            <div>
                                                {getRepeatEveryText(details.repeat_days)}
                                                {details.end_date && (
                                                    <span>
                                                        {" "}
                                                        until {DateTimeUtil.formatWithDayjs(details.end_date, "MMM D, YYYY")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Reason */}
                            {details.note && details.note.trim() !== "" && (
                                <div className="pt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Note
                                    </label>
                                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                        <p className="text-sm text-gray-700">{details.note}</p>
                                    </div>
                                </div>
                            )}

                            {/* Withdrawal note (from date_notes) - when present */}
                            {dateStatusInfo.dateNote != null && String(dateStatusInfo.dateNote).trim() !== "" && (
                                <div className="pt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Withdrawal Note
                                    </label>
                                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                        <p className="text-sm text-gray-700">{String(dateStatusInfo.dateNote)}</p>
                                    </div>
                                </div>
                            )}

                            {/* Department */}
                            {details.department && (
                                <div className="pt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Department
                                    </label>
                                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                        <p className="text-sm text-gray-700">{details.department}</p>
                                    </div>
                                </div>
                            )}

                            {/* Approval History */}
                            {details.workflow_details && details.workflow_details.length > 0 && (
                                <div className="pt-4">
                                    <ApprovalHistory
                                        workflowDetails={details.workflow_details}
                                        title="Approval Progress"
                                        showTitle={true}
                                    />
                                </div>
                            )}
                        </>
                        );
                    })()}
                </div>

                {/* Withdraw notes step - shown when user clicks Withdraw */}
                {showWithdrawNotes && details && data?.unavail?.start_date && (() => {
                    const slotId = details.employee_unavailability_slot_id;
                    if (!slotId) return null;
                    return (
                        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1501]">
                            <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
                                <h4 className="text-base font-semibold text-gray-900 mb-2">Withdraw unavailability</h4>
                                <p className="text-sm text-gray-600 mb-3">Add a note (optional):</p>
                                <textarea
                                    value={withdrawNotes}
                                    onChange={(e) => setWithdrawNotes(e.target.value)}
                                    placeholder="e.g. Withdrawn by employee"
                                    disabled={isWithdrawing}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60 disabled:bg-gray-50"
                                    rows={3}
                                />
                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        type="button"
                                        disabled={isWithdrawing}
                                        onClick={() => {
                                            if (isWithdrawing) return;
                                            setShowWithdrawNotes(false);
                                            setWithdrawNotes("");
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isWithdrawing}
                                        onClick={() => {
                                            if (isWithdrawing) return;
                                            onWithdraw?.({
                                                unavailabilitySlotId: slotId,
                                                date: data.unavail!.start_date!,
                                                notes: withdrawNotes.trim() || undefined,
                                            });
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-blue-600 bg-transparent border border-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 min-w-[140px]"
                                    >
                                        {isWithdrawing ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                                Withdrawing...
                                            </>
                                        ) : (
                                            "Confirm Withdraw"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Modal Footer */}
                <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        {/* Review request: when notification_transaction_id is present and user is available */}
                        {details && details.notification_transaction_id != null && String(details.notification_transaction_id).trim() !== "" && user?.os_emp_account_id != null && (
                            <button
                                type="button"
                                onClick={() => {
                                    const transactionId = String(details!.notification_transaction_id!).trim();
                                    const employeeId = Number(user!.os_emp_account_id);
                                    try {
                                        localStorage.setItem(
                                            "notification_detail_pending",
                                            JSON.stringify({
                                                category: "EMPLOYEE_UNAVAILABLITY",
                                                transactionId,
                                                employeeId,
                                            })
                                        );
                                    } catch (_) {}
                                    onClose();
                                    window.open("/notificationDetail", "_blank", "noopener,noreferrer");
                                }}
                                className="px-4 py-2 text-sm font-medium text-blue-600 bg-transparent border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors inline-flex items-center gap-2"
                            >
                                <ExternalLink className="w-4 h-4 flex-shrink-0" />
                                Review request
                            </button>
                        )}
                        {details && data?.unavail?.start_date && !showWithdrawNotes && (() => {
                            const dateStatusInfo = getDateStatusInfo(details, data.unavail.start_date);
                            const slotId = details.employee_unavailability_slot_id;
                            const startDateStr = data.unavail.start_date;
                            const [y, m, d] = startDateStr.split("-").map(Number);
                            const slotDate = new Date(y, m - 1, d);
                            const notPastDate = !SchedulingToolUtils.isPastDate(slotDate);
                            const isSuperAdmin = user ? SchedulingToolUtils.isSuperAdmin(user) : false;
                            const isApprovalActor = isApprovalActorInWorkflow(user, details.workflow_details);
                            const isEmployee = isUnavailabilityEmployee(user, details);
                            const canShowWithdrawButton = notPastDate && (
                                dateStatusInfo.dateStatus === 0
                                    ? isEmployee
                                    : (isSuperAdmin || isApprovalActor || isEmployee)
                            );
                            if (dateStatusInfo.canWithdraw && slotId && canShowWithdrawButton) {
                                return (
                                    <button
                                        type="button"
                                        onClick={() => setShowWithdrawNotes(true)}
                                        className="px-4 py-2 text-sm font-medium text-red-700 bg-transparent border-2 border-red-500 rounded-lg hover:bg-red-50 transition-colors"
                                    >
                                        Withdraw Request
                                    </button>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors ml-auto"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnavailabilityDetailModal;
